<template>
  <div class="bg-base-100 mt-2 space-y-6 p-6">
    <div class="flex items-center justify-between">
      <div>
        <div class="text-lg font-bold">Tổng quan chiến dịch</div>
        <div class="text-base-content/80">
          Các chỉ số chính: máy trạm, cập nhật và bộ cài đặt của chiến dịch.
        </div>
      </div>
      <button
        class="btn btn-sm btn-outline"
        :disabled="isLoading"
        @click="refresh"
      >
        <i
          class="fa-solid fa-rotate opacity-90"
          :class="{ 'fa-spin': isLoading }"
        ></i>
        Làm mới
      </button>
    </div>

    <div v-if="isError" class="text-error">Lỗi khi tải dữ liệu thống kê.</div>

    <!-- Stat cards -->
    <div class="stats stats-vertical shadow md:stats-horizontal">
      <div class="stat">
        <div class="stat-title">Tổng máy trạm</div>
        <div class="stat-value">
          <span
            v-if="isLoading"
            class="bg-base-300 inline-block h-8 w-16 animate-pulse rounded"
          ></span>
          <template v-else>{{ agentTotal }}</template>
        </div>
        <div class="stat-desc">{{ agentUninstalled }} máy đã gỡ cài đặt</div>
      </div>

      <div class="stat">
        <div class="stat-title">Online</div>
        <div class="stat-value text-success">
          <span
            v-if="isLoading"
            class="bg-base-300 inline-block h-8 w-12 animate-pulse rounded"
          ></span>
          <template v-else>{{ agentOnline }}</template>
        </div>
        <div class="stat-desc">{{ onlinePercent }}% tổng số máy</div>
      </div>

      <div class="stat">
        <div class="stat-title">Offline</div>
        <div class="stat-value text-base-content/60">
          <span
            v-if="isLoading"
            class="bg-base-300 inline-block h-8 w-12 animate-pulse rounded"
          ></span>
          <template v-else>{{ agentOffline }}</template>
        </div>
        <div class="stat-desc">Ngắt kết nối trên 1 phút</div>
      </div>

      <div class="stat">
        <div class="stat-title">Yêu cầu cập nhật</div>
        <div class="stat-value">
          <span
            v-if="isLoading"
            class="bg-base-300 inline-block h-8 w-12 animate-pulse rounded"
          ></span>
          <template v-else>{{ updateTotal }}</template>
        </div>
        <div class="stat-desc">{{ installerTotal }} bộ cài đặt</div>
      </div>

      <div class="stat">
        <div class="stat-title">Hoạt động thực hiện</div>
        <div class="stat-value">
          <span
            v-if="isLoading"
            class="bg-base-300 inline-block h-8 w-12 animate-pulse rounded"
          ></span>
          <template v-else>{{ totalActions }}</template>
        </div>
        <div class="stat-desc">{{ screenshotsCount }} ảnh chụp</div>
      </div>
    </div>

    <div class="grid gap-6 lg:grid-cols-2">
      <!-- Online/offline breakdown -->
      <div class="space-y-4 p-5 shadow transition-all hover:shadow-md">
        <div class="font-bold">Trạng thái máy trạm</div>
        <div v-if="isLoading" class="py-6 text-center opacity-70">
          Đang tải...
        </div>
        <div v-else-if="agentTotal === 0" class="py-6 text-center opacity-70">
          Chưa có máy trạm nào trong chiến dịch.
        </div>
        <div v-else class="space-y-4">
          <div>
            <div class="mb-1 flex justify-between text-sm">
              <span class="text-success">Online</span>
              <span class="font-bold">{{ agentOnline }}</span>
            </div>
            <progress
              class="progress progress-success w-full"
              max="100"
              :value="onlinePercent"
            ></progress>
          </div>
          <div>
            <div class="mb-1 flex justify-between text-sm">
              <span class="text-base-content/60">Offline</span>
              <span class="font-bold">{{ agentOffline }}</span>
            </div>
            <progress
              class="progress w-full"
              max="100"
              :value="offlinePercent"
            ></progress>
          </div>
        </div>
      </div>

      <!-- Update status breakdown -->
      <div class="space-y-4 p-5 shadow transition-all hover:shadow-md">
        <div class="font-bold">Cập nhật theo trạng thái</div>
        <div v-if="isLoading" class="py-6 text-center opacity-70">
          Đang tải...
        </div>
        <div
          v-else-if="updateStatusBuckets.length === 0"
          class="py-6 text-center opacity-70"
        >
          Chưa có yêu cầu cập nhật nào.
        </div>
        <div v-else class="space-y-4">
          <div v-for="bucket in updateStatusBuckets" :key="bucket.status">
            <div class="mb-1 flex justify-between text-sm">
              <span>{{ statusLabels[bucket.status] ?? bucket.status }}</span>
              <span class="font-bold">{{ bucket.count }}</span>
            </div>
            <progress
              class="progress w-full"
              :class="statusProgressClass[bucket.status] ?? ''"
              max="100"
              :value="percentOfTotal(bucket.count, updateTotal)"
            ></progress>
          </div>
          <div
            v-if="latestInstaller"
            class="border-base-300 border-t pt-4 text-sm"
          >
            <span class="opacity-70">Bộ cài đặt mới nhất:</span>
            <span class="font-bold">{{ latestInstaller.name }}</span>
            <span class="opacity-70">
              ({{
                dayjs(latestInstaller.created_at).format("YYYY-MM-DD HH:mm")
              }})
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Action type breakdown + daily trend -->
    <div class="grid gap-6 lg:grid-cols-2">
      <!-- Breakdown by action type -->
      <div class="space-y-4 p-5 shadow transition-all hover:shadow-md">
        <div class="flex items-center justify-between gap-4">
          <div class="font-bold">Theo loại hoạt động</div>
          <div class="btn-group" role="group" aria-label="Khoảng thời gian">
            <button
              v-for="opt in periodOptions"
              :key="opt.value"
              class="btn btn-sm"
              :class="{ 'btn-active': activityPeriod === opt.value }"
              :aria-pressed="activityPeriod === opt.value"
              @click="activityPeriod = opt.value"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>
        <div v-if="isLoading" class="py-6 text-center opacity-70">
          Đang tải...
        </div>
        <div
          v-else-if="actionTypeBuckets.length === 0"
          class="py-6 text-center opacity-70"
        >
          Chưa có hoạt động thực hiện nào trong chiến dịch.
        </div>
        <div v-else class="space-y-4">
          <div
            v-for="bucket in actionTypeBuckets"
            :key="bucket.type"
            class="border-base-300 border-b last:border-0 pb-2 last:pb-0"
          >
            <div class="mb-1 flex justify-between text-sm">
              <span class="capitalize">{{ actionLabels[bucket.type] ?? bucket.type }}</span>
              <span class="font-bold">{{ bucket.count }}</span>
            </div>
            <progress
              class="progress w-full"
              :value="percentOfTotal(bucket.count, totalActions)"
              max="100"
            ></progress>
          </div>
          <div
            v-if="topActionType"
            class="border-base-300 border-t pt-4 text-sm"
          >
            <span class="opacity-70">Hoạt động nhiều nhất:</span>
            <span class="font-bold capitalize">{{
              actionLabels[topActionType.type] ?? topActionType.type
            }}</span>
            <span class="opacity-70"> ({{ topActionType.count }} lần)</span>
          </div>
        </div>
      </div>

      <!-- Daily trend -->
      <div class="space-y-4 p-5 shadow transition-all hover:shadow-md">
        <div class="flex items-center justify-between gap-4">
          <div class="font-bold">Xu hướng hoạt động theo ngày</div>
          <div class="btn-group" role="group" aria-label="Khoảng thời gian">
            <button
              v-for="opt in periodOptions"
              :key="opt.value"
              class="btn btn-sm"
              :class="{ 'btn-active': activityPeriod === opt.value }"
              :aria-pressed="activityPeriod === opt.value"
              @click="activityPeriod = opt.value"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>
        <div v-if="isLoading" class="py-6 text-center opacity-70">
          Đang tải...
        </div>
        <div v-else-if="activityTrend.length === 0" class="py-6 text-center opacity-70">
          Chưa có dữ liệu hoạt động theo ngày.
        </div>
        <div v-else class="space-y-2">
          <div
            v-for="day in activityTrend"
            :key="day.day"
            class="flex justify-between text-sm"
          >
            <span>{{ day.day }}</span>
            <span class="font-bold">{{ day.total }}</span>
          </div>
          <div class="border-base-300 border-t pt-4 text-xs opacity-80">
            Tổng cộng {{ trendTotal }} lần thực hiện qua {{ activityTrend.length }} ngày.
          </div>
        </div>
      </div>
    </div>

    <!-- Recent update activity -->
    <div class="space-y-4 p-5 shadow transition-all hover:shadow-md">
      <div class="font-bold">Cập nhật gần đây</div>
      <div v-if="isLoading" class="py-6 text-center opacity-70">
        Đang tải...
      </div>
      <div
        v-else-if="recentUpdates.length === 0"
        class="py-6 text-center opacity-70"
      >
        Chưa có hoạt động cập nhật nào.
      </div>
      <div v-else class="overflow-x-auto">
        <table class="table">
          <thead>
            <tr>
              <th>STT</th>
              <th>Thời gian</th>
              <th>Phiên bản</th>
              <th>Loại</th>
              <th>Trạng thái</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in recentUpdates" :key="item.id || index">
              <td>{{ index + 1 }}</td>
              <td>{{ dayjs(item.created).format("YYYY-MM-DD HH:mm:ss") }}</td>
              <td>{{ item.version }}</td>
              <td>{{ item.is_campaign ? "Chiến dịch" : "Máy trạm" }}</td>
              <td>
                <ActionStatusBadge :status="item.status" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ActionStatusBadge from "@/components/ActionStatusBadge.vue";
import { useCampaignDashboard, type ActivityPeriod } from "@/composables/useCampaignDashboard";
import { router } from "@/router/router";
import dayjs from "dayjs";
import { computed } from "vue";

const campaignId = String(router.currentRoute.value.params.campaign_id);

const {
  isLoading,
  isError,
  refetch,
  agentTotal,
  agentOnline,
  agentOffline,
  agentUninstalled,
  onlinePercent,
  updateTotal,
  updateStatusBuckets,
  recentUpdates,
  installerTotal,
  latestInstaller,
  totalActions,
  screenshotsCount,
  actionTypeBuckets,
  topActionType,
  activityTrend,
  activityPeriod,
} = useCampaignDashboard(campaignId);

const periodOptions: { value: ActivityPeriod; label: string }[] = [
  { value: 0, label: "Toàn bộ" },
  { value: 1, label: "Hôm nay" },
  { value: 7, label: "7 ngày" },
  { value: 30, label: "30 ngày" },
];

const offlinePercent = computed(() =>
  agentTotal.value > 0
    ? Math.round((agentOffline.value / agentTotal.value) * 100)
    : 0,
);

const percentOfTotal = (count: number, total: number) =>
  total > 0 ? Math.round((count / total) * 100) : 0;

const statusLabels: Record<string, string> = {
  pending: "Chờ xử lý",
  "in-progress": "Đang xử lý",
  complete: "Hoàn tất",
  success: "Thành công",
  stop: "Đang dừng",
  stopped: "Đã dừng",
  error: "Lỗi",
};

const statusProgressClass: Record<string, string> = {
  pending: "progress-warning",
  "in-progress": "progress-info",
  complete: "progress-success",
  success: "progress-success",
  stop: "progress-warning",
  stopped: "progress-error",
  error: "progress-error",
};

const actionLabels: Record<string, string> = {
  app_install: "Cài đặt ứng dụng",
  explore: "Duyệt tệp",
  open_port: "Quét cổng",
  key_logger: "Bám phím",
  screenshot: "Ảnh chụp",
  browser_history: "Lịch sử duyệt web",
  system_cmd: "Lệnh hệ thống",
  process: "Tiền trình",
  uninstall: "Gỡ cài đặt",
  update_agent: "Cập nhật tác nhân",
  chat_history: "Lịch sử tin nhắn",
  last_activity: "Hoạt động gần đây",
  recon: "Tái cấu hình",
  password_detection: "Phát hiện mật khẩu",
  nmap: "Quét mạng",
  bruteforce: "Bẻ khóa",
  spread_lan: "Lan truyền LAN",
  dump_credential: "Đánh cắp mật khẩu",
  interactive_terminal: "Terminal tương tác",
  record: "Ghi âm",
};

const trendTotal = computed(
  () => activityTrend.value.reduce((sum, d) => sum + d.total, 0),
);

const refresh = () => refetch();
</script>
